import {
  isAndroid,
  isChrome,
  isIosChrome,
  isIphone,
  isMobileApp,
  isSafari,
  isSamsung,
} from 'client-portal/utils/mobile-detect';
import { isTestingOrMirage } from 'ember-simplepractice/utils/is-testing';
import { tracked } from '@glimmer/tracking';
import ENV from 'client-portal/config/environment';
import Service, { service } from '@ember/service';

const CUSTOM_URL_SCHEME = 'simplepracticeclients:client-app';
const ANDROID_APP_SUFFIX = '/client-app';
const IOS_APP_SUFFIX = '/pageLink';

export default class ClientAppBannerService extends Service {
  @service analytics;
  @service mixpanel;
  @service session;
  @service currentPractice;
  @service fastboot;
  @service router;

  @tracked showBanner = false;
  @tracked clientAppBannerDismissed = false;
  @tracked location = null;
  @tracked documentId = null;

  get #isTestingOrMirage() {
    if (this.fastboot.isFastBoot) {
      return false;
    }

    return isTestingOrMirage() && ENV.railsEnv !== 'test';
  }

  get #isStaging() {
    return ENV.environment === 'staging';
  }

  get #isProduction() {
    return ENV.environment === 'production';
  }

  get hasIosFeature() {
    return this.currentPractice.get('featureIosClientappPromotionBanner');
  }

  get clientAccess() {
    return this.session.currentClientAccess;
  }

  get currentSignedInClient() {
    return this.clientAccess?.client;
  }

  get currentClient() {
    return this.session.currentClient;
  }

  get canDisplay() {
    return this.isIphoneSafariOrChrome || this.isAndroidChromeOrSamsung;
  }

  get isIphoneSafariOrChrome() {
    return this.hasIosFeature && isIphone() && (isSafari() || isIosChrome());
  }

  get isAndroidChromeOrSamsung() {
    return isAndroid() && (isChrome() || isSamsung());
  }

  resetDismiss() {
    this.clientAppBannerDismissed = false;
  }

  display({ location, documentId }) {
    let { session, clientAppBannerDismissed, canDisplay } = this;
    let isDocumentsRedirect =
      session.isDocumentsRouteRedirect || session.get('data.redirect')?.includes('documents');
    let isRedirectedFromOldAppVersions = isDocumentsRedirect && !isMobileApp();

    if (canDisplay && !clientAppBannerDismissed && !isRedirectedFromOldAppVersions) {
      this.showBanner = true;
      this.location = location;
      this.documentId = documentId;
    }
  }

  hide() {
    this.clientAppBannerDismissed = true;
    this.showBanner = false;
  }

  trackDismissEvent() {
    let { location, currentSignedInClient, mixpanel, analytics, isAndroidChromeOrSamsung } = this;

    let analyticsEventName = 'Client App Banner Dismissed';
    let eventProperties = {
      'click_location': location || '',
    };

    if (isAndroidChromeOrSamsung) {
      eventProperties = {
        ...eventProperties,
        'client_portal_client_access_id': currentSignedInClient?.id || '',
      };
    }

    let analyticsProperties = {
      object: 'Client App Banner',
      action: 'Dismissed',
      ...eventProperties,
    };

    mixpanel.track('user: dismissed client app banner', eventProperties);

    currentSignedInClient
      ? analytics.track(analyticsEventName, analyticsProperties)
      : analytics.trackAnonymously(analyticsEventName, analyticsProperties);
  }

  trackAccessAppEvent() {
    let { location, currentSignedInClient, mixpanel, analytics } = this;

    let analyticsEventName = 'App Accessed from Client App Banner';
    let eventProperties = {
      'client_portal_client_access_id': currentSignedInClient?.id || '',
      'click_location': location || '',
    };
    let analyticsProperties = {
      object: 'App',
      action: 'Accessed',
      ...eventProperties,
    };

    mixpanel.track('user: accessed app from client app banner', eventProperties);

    currentSignedInClient
      ? analytics.track(analyticsEventName, analyticsProperties)
      : analytics.trackAnonymously(analyticsEventName, analyticsProperties);
  }

  visitAppStore() {
    let { currentSignedInClient, currentClient, location, documentId } = this;

    this.trackAccessAppEvent();

    let query = new URLSearchParams();
    if (currentSignedInClient) {
      let { hashedId: clientHashedId } = currentSignedInClient;
      let { hashedId: profileId } = currentClient;
      if (clientHashedId) query.set('client_id', clientHashedId);
      if (profileId) query.set('profile_id', profileId);
    }

    if (location) query.set('page', location);
    if (documentId) query.set('document_id', documentId);

    query.set('universalLink', 'true');

    this.showBanner = false;

    this.#useUniversalDeepLinks(query);
  }

  #useUniversalDeepLinks(query) {
    let { isAndroidChromeOrSamsung, isIphoneSafariOrChrome } = this;
    let queryString = `?${query.toString()}`;
    let universalLinkGenerator = this.#getUniversalLink(queryString);

    if (this.#isTestingOrMirage) {
      let queryParams = {
        universalLink: encodeURIComponent(universalLinkGenerator()),
      };
      for (let [key, value] of query.entries()) {
        queryParams[key] = value;
      }

      this.router.transitionTo('client-app', { queryParams });
      return;
    }

    if (isIphoneSafariOrChrome) {
      let fallbackUrl = universalLinkGenerator(IOS_APP_SUFFIX);
      this.#openDeepLink(`${CUSTOM_URL_SCHEME}${queryString}`, {
        fallbackUrl,
        fallbackTimout: 900,
        replace: true,
      });
    } else if (isAndroidChromeOrSamsung) {
      this.#openDeepLink(universalLinkGenerator(ANDROID_APP_SUFFIX));
    } else {
      window.location.href = universalLinkGenerator();
    }
  }

  #getUniversalLink(queryString) {
    return (suffix = '') => {
      return `${this.#deepLinkBase}${suffix}${queryString}`;
    };
  }

  #openDeepLink(deepLink, { fallbackUrl, replace = true, fallbackTimout = 900 } = {}) {
    const nav = url => (replace ? window.location.replace(url) : window.location.assign(url));

    nav(deepLink);

    if (fallbackUrl && !/^https?:/i.test(deepLink)) {
      /* eslint-disable ember-cleanup/no-settimeout */
      const t = setTimeout(() => nav(fallbackUrl), fallbackTimout);

      const cancel = () => clearTimeout(t);
      /* eslint-enable ember-cleanup/no-settimeout */
      window.addEventListener('pagehide', cancel, { once: true });
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState === 'hidden') cancel();
        },
        { once: true }
      );
    }
  }

  get #origin() {
    if (this.fastboot?.isFastBoot) {
      const headers = this.fastboot.request.headers;
      const host = headers.get('x-forwarded-host') || headers.get('host');
      const proto = headers.get('x-forwarded-proto') || 'https';
      return `${proto}://${host}`;
    } else {
      const { protocol, host } = window.location;
      return `${protocol}//${host}`;
    }
  }

  get #deepLinkBase() {
    if (this.#isStaging || this.#isProduction) {
      return ENV.universalLinkHost;
    } else {
      return this.#origin;
    }
  }
}
